/**
 * ============================================================================
 * /me — "My record". The participant's own completion record, on their phone.
 * ============================================================================
 * Scene 11 from the attendee's seat. The instructor taps Award completions and
 * this page — open on thirty phones — updates: course, date, hours, outcome.
 * That is the "tracker updates" moment, seen by the people it is about.
 *
 * Signed in by the personal key (M25) and nothing else. No key on this phone
 * means no record to show, and the page says exactly how to get one.
 *
 * What it shows is the boundary object and nothing more (Decision #4): what
 * was earned, when, how many hours. Never scores, never answers. Expiry
 * windows (PHTLS 4 yr, ACLS 2 yr…) belong to the course catalog and are not
 * here yet — this page says what has been earned, not what is due.
 * ============================================================================
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { participantFetch, readPersonalKey } from '../lib/participant.js';
import ErrorBanner from '../components/ErrorBanner.jsx';

const DATE = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeZone: 'America/New_York' });

const DAY = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeZone: 'UTC' });
// A bare date (a card's issue date) has no time zone and must not slide a day.
function when(iso) {
  if (!iso) return '';
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? DAY.format(new Date(`${iso}T00:00:00Z`)) : DATE.format(new Date(iso));
}

function hours(n) {
  return `${Number(n).toFixed(n % 1 === 0 ? 0 : 2)} h`;
}

/** How a session that has not been awarded yet reads to the person in it. */
function pendingLabel(status) {
  switch (status) {
    case 'active':
      return 'In progress';
    case 'closed':
      return 'Awaiting sign-off';
    case 'scheduled':
      return 'Checked in';
    default:
      return status;
  }
}

const STAND_CLS = { current: 'badge--ok', due_soon: 'badge--due', expired: 'badge--warn', never: 'badge--warn' };
const STAND_TXT = { current: '✓ Current', due_soon: '● Due soon', expired: '✖ Expired', never: '✖ Not on record' };
const CARD_CLS = { verified: 'badge--ok', pending: 'badge--due', rejected: 'badge--warn' };
const CARD_TXT = { verified: '✓ Verified', pending: '● Being checked', rejected: '✖ Not accepted' };

/**
 * "I have a card from somewhere else." Files a pending card against this
 * phone's own record; staff verify it on the dashboard before it counts.
 */
function CardsFromElsewhere({ cards, onChange }) {
  const [open, setOpen] = useState(false);
  const [courses, setCourses] = useState(null);
  const [form, setForm] = useState({ course_id: '', issued_at: '', expires_at: '', issuer: '' });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (open && courses === null)
      participantFetch('/api/v1/checkin/courses')
        .then(setCourses)
        .catch((e) => setError(e));
  }, [open, courses]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const card = await participantFetch('/api/v1/checkin/credentials', {
        method: 'POST',
        body: JSON.stringify({
          course_id: form.course_id,
          issued_at: form.issued_at,
          expires_at: form.expires_at || null,
          issuer: form.issuer || null,
        }),
      });
      if (file) {
        await participantFetch(`/api/v1/checkin/credentials/${card.id}/file`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/octet-stream', 'x-file-type': file.type || 'application/octet-stream' },
          body: file,
        });
      }
      onChange();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };
  return (
    <section aria-label="Cards from elsewhere">
      <h2 className="record__h2">Cards from elsewhere</h2>
      {cards.length > 0 && (
        <ul className="record__list">
          {cards.map((c) => (
            <li key={c.id} className="card record__item">
              <div className="record__item-main">
                <strong>{c.course_code}</strong>
                <span className="muted">
                  issued {when(c.issued_at)}
                  {c.issuer ? ` · ${c.issuer}` : ''}
                  {c.status === 'rejected' && c.rejection_reason ? ` · ${c.rejection_reason}` : ''}
                </span>
              </div>
              <span className={`badge ${CARD_CLS[c.status]}`}>{CARD_TXT[c.status]}</span>
            </li>
          ))}
        </ul>
      )}
      {!open ? (
        <p className="muted">
          Took a class somewhere else? <button type="button" className="btn btn--quiet" onClick={() => setOpen(true)}>Add a card</button>{' '}
          — a photo of it is enough. Your training office checks it and it counts from then on.
        </p>
      ) : (
        <form className="card record__cardform" onSubmit={submit}>
          <ErrorBanner error={error} />
          <label className="field">
            <span>Which course</span>
            <select value={form.course_id} onChange={(e) => setForm({ ...form, course_id: e.target.value })} required>
              <option value="">{courses ? 'Choose…' : 'Loading…'}</option>
              {(courses ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} — {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Date on the card (issued)</span>
            <input type="date" value={form.issued_at} onChange={(e) => setForm({ ...form, issued_at: e.target.value })} required />
          </label>
          <label className="field">
            <span>Expires (if printed)</span>
            <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
          </label>
          <label className="field">
            <span>Who issued it (optional)</span>
            <input type="text" value={form.issuer} onChange={(e) => setForm({ ...form, issuer: e.target.value })} placeholder="AHA, NAEMT…" />
          </label>
          <label className="field">
            <span>Photo of the card</span>
            <input type="file" accept="image/*,application/pdf" capture="environment" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </label>
          <div className="record__cardform-actions">
            <button type="submit" className="btn btn--primary" disabled={busy || !form.course_id || !form.issued_at}>
              {busy ? 'Sending…' : 'Send it in'}
            </button>
            <button type="button" className="btn btn--quiet" onClick={() => setOpen(false)} disabled={busy}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

export function MyRecordPage() {
  const [hasKey] = useState(() => Boolean(readPersonalKey()));
  const [record, setRecord] = useState(null);
  const [error, setError] = useState(null);

  /**
   * Polled while anything is pending, because the whole point is watching the
   * award land. A quiet record is fetched once. A failed poll keeps the last
   * good record on screen — a blink of signal must not blank someone's
   * transcript in front of them.
   */
  useEffect(() => {
    if (!hasKey) return undefined;
    let active = true;
    let timer = null;

    const tick = async () => {
      try {
        const data = await participantFetch('/api/v1/checkin/record');
        if (!active) return;
        setRecord(data);
        setError(null);
        if (data.pending?.length) timer = setTimeout(tick, 4000);
      } catch (err) {
        if (!active) return;
        if (!record) setError(err);
        timer = setTimeout(tick, 8000);
      }
    };

    tick();
    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasKey]);

  if (!hasKey) {
    return (
      <main className="checkin">
        <div className="card">
          <h1>My record</h1>
          <p>This phone doesn’t know you yet.</p>
          <p className="muted">
            Scan a class code and check in. After that, your record will be here — and on any
            phone you open your check-in link on.
          </p>
        </div>
      </main>
    );
  }

  if (error && !record) {
    return (
      <main className="checkin">
        <div className="card">
          <h1>My record</h1>
          <p>{error.message}</p>
          {error.code === 'NOT_RECOGNIZED' && (
            <p className="muted">
              This phone’s link has expired or was forgotten. Scan a class code and check in again,
              or ask for a new link by email at the next check-in.
            </p>
          )}
        </div>
      </main>
    );
  }

  if (!record) {
    return (
      <main className="checkin" aria-busy="true">
        <p className="muted">Loading your record…</p>
      </main>
    );
  }

  const { person, records, pending, courses = [], total_hours: totalHours, standing = [], cards = [] } = record;

  return (
    <main className="checkin">
      <header className="checkin__head">
        <h1>My record</h1>
        <p className="record__who">
          <strong>{person.full_name}</strong>
          <br />
          {person.external_id ? (
            <span className="muted">A-B Tech ID {person.external_id}</span>
          ) : (
            <span className="muted">No A-B Tech ID on file yet — the office will confirm it</span>
          )}
        </p>
      </header>

      <div className="card record__total">
        <span className="record__total-number">{hours(totalHours)}</span>
        <span className="muted">earned, {records.filter((r) => r.outcome === 'complete').length} completed</span>
      </div>

      {standing.length > 0 && (
        <section aria-label="What your agency requires">
          <h2 className="record__h2">What your agency requires</h2>
          {standing.map((st) => (
            <div key={st.agency} className="card record__standing">
              <p className="record__standing-head">
                <strong>{st.agency}</strong>
                {st.level && <span className="muted"> · {st.level}</span>}
                {st.lanes?.length > 0 && <span className="muted"> · {st.lanes.join(', ')}</span>}
              </p>
              {st.red_tier === 'expired' && (
                <p className="banner banner--error">
                  ✖ Your state credential expired {when(st.state_credential_expires)}. You cannot work until it is renewed — your
                  agency will be in touch.
                </p>
              )}
              {st.red_tier === 'due_soon' && (
                <p className="record__note">● Your state credential expires {when(st.state_credential_expires)}.</p>
              )}
              {st.credentials.length === 0 ? (
                <p className="muted">Nothing is required of you at this agency yet.</p>
              ) : (
                <ul className="record__list">
                  {st.credentials.map((c) => (
                    <li key={c.rule_id} className="record__item record__item--flat">
                      <div className="record__item-main">
                        <strong>{c.course_code}</strong>
                        <span className="muted">
                          every {c.interval_months} months{c.lane ? ` (${c.lane})` : ''}
                        </span>
                      </div>
                      <div className="record__item-side">
                        <span className={`badge ${STAND_CLS[c.standing]}`}>{STAND_TXT[c.standing]}</span>
                        <span className="muted record__until">
                          {c.due_at ? `${c.standing === 'expired' ? 'ran out' : 'due'} ${when(c.due_at)}` : 'no completion on record'}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {st.hours?.required != null && (
                <p className="muted record__note">
                  CE this cycle: {hours(st.hours.total)} of {st.hours.required} h
                  {st.trajectory?.status === 'on_pace' || st.trajectory?.status === 'complete'
                    ? ' — on track'
                    : st.trajectory?.status === 'behind'
                      ? ' — a little behind pace'
                      : st.trajectory?.status === 'cant_make'
                        ? ' — behind; talk to your training officer about catching up'
                        : ''}
                </p>
              )}
            </div>
          ))}
        </section>
      )}

      {standing.length > 0 && (
        <p className="record__note">
          <Link to={`/me/transcript?agency=${encodeURIComponent(standing[0].agency)}`}>My CE transcript</Link>
          <span className="muted"> — every course with its hours and categories, ready to type into NREMT or Continuum.</span>
        </p>
      )}

      <CardsFromElsewhere cards={cards} onChange={() => window.location.reload()} />

      {courses.length > 0 && (
        <section aria-label="Where you stand">
          <h2 className="record__h2">Where you stand</h2>
          <ul className="record__list">
            {courses.map((c) => (
              <li key={c.code} className="card record__item">
                <div className="record__item-main">
                  <strong>{c.code}</strong>
                  <span className="muted">{c.name}</span>
                </div>
                <div className="record__item-side">
                  {c.expires_at ? (
                    <>
                      <span className={`badge ${c.expired ? 'badge--warn' : 'badge--ok'}`}>
                        {c.expired ? 'Expired' : 'Current'}
                      </span>
                      <span className="muted record__until">
                        {c.expired ? 'ran out' : 'good until'} {when(c.expires_at)}
                      </span>
                    </>
                  ) : (
                    <span className="badge badge--ok">Complete</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {pending.length > 0 && (
        <section aria-label="In progress">
          <h2 className="record__h2">Today</h2>
          <ul className="record__list" aria-live="polite">
            {pending.map((p) => (
              <li key={p.session_id} className="card record__item record__item--pending">
                <div className="record__item-main">
                  <strong>{p.section.title}</strong>
                  <span className="muted">
                    {p.section.code && `${p.section.code} · `}
                    {when(p.scheduled_start)}
                  </span>
                </div>
                <span className="badge">{pendingLabel(p.status)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section aria-label="Completed">
        <h2 className="record__h2">Completed</h2>
        {records.length === 0 ? (
          <p className="muted">Nothing awarded yet. Completions appear here once your instructor signs off.</p>
        ) : (
          <ul className="record__list">
            {records.map((r) => (
              <li key={r.id} className="card record__item">
                <div className="record__item-main">
                  <strong>{r.section.title}</strong>
                  <span className="muted">
                    {r.section.code && `${r.section.code} · `}
                    {when(r.scheduled_start)}
                  </span>
                </div>
                <div className="record__item-side">
                  <span className={`badge ${r.outcome === 'complete' ? 'badge--ok' : 'badge--warn'}`}>
                    {r.outcome === 'complete' ? 'Complete' : 'Incomplete'}
                  </span>
                  <span className="record__hours">{hours(r.ce_hours)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <p className="muted checkin__footer">
        This is your attendance and completion record with Attest. Scores and answers are never
        shown here or shared with your agency.{' '}
        <Link to="/checkin">Check in to a class</Link>
      </p>
    </main>
  );
}

export default MyRecordPage;
